import React, { useEffect, useId, useRef, useState } from 'react';
import Icon from '../../shared/Icon';
import LibraryDialog from '../../Library/LibraryDialog';
import Select from '../../shared/Select';
import { pickableDocuments } from '../../../../models/LibraryModel';
import '../../Library/AddDocumentDialog.css'; // library-form-group, the dialog's select width
import './Scenarios.css';

// A picker's options (PRD-1702, the ResUI Select): "- Select -" becomes its
// placeholder, or "- none available -" when nothing of the type can be picked.
// The Select drops the placeholder from its menu, so an optional picker also
// leads with "None", the way to clear it. A document picked in another row
// isn't offered again (`taken`), so the rows always match what saves.
function documentOptions(documents, type, value, { taken = [], optional = false } = {}) {
  const list = pickableDocuments(documents, type, [value]).filter((d) => d.id === value || !taken.includes(d.id));
  if (!list.length) return [{ value: '', label: '- none available -' }];
  return [
    { value: '', label: '- Select -' },
    ...(optional ? [{ value: '', label: 'None' }] : []),
    ...list.map((d) => ({ value: d.id, label: d.name })),
  ];
}

/*
 * A scenario carries one narrative but any number of reference keys and SOPs,
 * so those two are lists of pickers. A row can be removed while there is more
 * than one; an optional list's last row can go too. The removed row's button
 * goes with it, so focus moves to the row above, or to "+ Add" from the first
 * row, and never leaves the dialog.
 */
function DocumentRows({ label, one, type, values, documents, onChange, optional = false }) {
  const rowId = useId();
  const addRef = useRef(null);
  const [refocus, setRefocus] = useState(null); // a row index, or 'add'
  useEffect(() => {
    if (refocus === null) return;
    (refocus === 'add' ? addRef.current : document.getElementById(`${rowId}-${refocus}`))?.focus();
    setRefocus(null);
  }, [refocus, rowId]);
  const set = (i, value) => onChange(values.map((v, j) => (j === i ? value : v)));
  const remove = (i) => {
    onChange(values.filter((_, j) => j !== i));
    setRefocus(i > 0 ? i - 1 : 'add');
  };
  return (
    <>
      {values.map((value, i) => (
        // The rows have no identity but their place.
        <div key={i} className="scenario-row-fields">
          <Select
            id={`${rowId}-${i}`}
            ariaLabel={`${label} ${i + 1}`}
            value={value}
            options={documentOptions(documents, type, value, { taken: values.filter((_, j) => j !== i), optional })}
            onChange={(next) => set(i, next)}
          />
          {(optional || values.length > 1) && (
            <button
              type="button"
              className="scenario-row-remove"
              aria-label={`Remove this ${one}`}
              onClick={() => remove(i)}
            >
              <Icon name="x" size={20} />
            </button>
          )}
        </div>
      ))}
      <div className="scenario-add-row">
        <button ref={addRef} type="button" className="resui-link scenario-add-inline" onClick={() => onChange([...values, ''])}>
          + Add {one}
        </button>
      </div>
    </>
  );
}

const Required = () => (
  <>
    <span className="resui-field-required" aria-hidden="true">*</span>
    <span className="library-sr-only">required</span>
  </>
);

// The picks that would be saved: blanks and repeats dropped, order kept.
const picked = (ids) => ids.filter((v, i) => v && ids.indexOf(v) === i);
const sameIds = (ids, saved) => {
  const a = picked(ids);
  return a.length === saved.length && a.every((id, i) => id === saved[i]);
};

const Optional = () => <span className="scenario-optional">(optional)</span>;

/**
 * Add scenario and Edit scenario (PRD-1701, Navy Phase 0 handoff). A name and
 * at least one reference key are required; the narrative and the SOPs are
 * optional. Only documents that finished uploading and aren't archived can be
 * picked. Save scenario stays disabled until the two required pieces are
 * there and, when editing, something has changed. Focus starts on the name;
 * the shell handles the rest (see LibraryDialog).
 */
function ScenarioDialog({ scenario, documents, opener, fallbackFocus, onSave, onClose }) {
  const ids = useId();
  const nameRef = useRef(null);
  const [name, setName] = useState(scenario?.name || '');
  const [narrativeId, setNarrativeId] = useState(scenario?.narrativeId || '');
  const [referenceKeyIds, setReferenceKeyIds] = useState(scenario?.referenceKeyIds.length ? scenario.referenceKeyIds : ['']);
  const [sopIds, setSopIds] = useState(scenario?.sopIds.length ? scenario.sopIds : ['']);
  // Save waits until the form is complete and, when editing, something has
  // changed (ResUI Design Decisions, Modal). Blank and repeated picks don't count.
  const complete = !!(name.trim() && referenceKeyIds.some(Boolean));
  const changed = !scenario
    || name.trim() !== scenario.name
    || (narrativeId || null) !== (scenario.narrativeId || null)
    || !sameIds(referenceKeyIds, scenario.referenceKeyIds)
    || !sameIds(sopIds, scenario.sopIds);
  const canSave = complete && changed;

  const save = () => {
    if (!canSave) return;
    onSave({ id: scenario?.id, name, narrativeId, referenceKeyIds, sopIds });
  };

  return (
    <LibraryDialog
      title={scenario ? 'Edit scenario' : 'Add scenario'}
      initialFocus={nameRef}
      opener={opener}
      fallbackFocus={fallbackFocus}
      onClose={onClose}
    >
      <div className="resui-field library-form-group">
        <label className="resui-field-label" htmlFor={`${ids}-name`}>Scenario name<Required /></label>
        <input
          ref={nameRef}
          id={`${ids}-name`}
          type="text"
          className="resui-input"
          placeholder="e.g. Strait Transit - Fishing Season"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="resui-field library-form-group">
        <label className="resui-field-label" htmlFor={`${ids}-narrative`}>Narrative<Optional /></label>
        <Select
          id={`${ids}-narrative`}
          className="resui-select-field--block"
          value={narrativeId}
          options={documentOptions(documents, 'narrative', narrativeId, { optional: true })}
          onChange={setNarrativeId}
        />
      </div>

      <div className="resui-field library-form-group" role="group" aria-labelledby={`${ids}-keys`}>
        <span className="resui-field-label" id={`${ids}-keys`}>Reference keys<Required /></span>
        <DocumentRows label="Reference key" one="reference key" type="reference_key" values={referenceKeyIds} documents={documents} onChange={setReferenceKeyIds} />
      </div>

      <div className="resui-field library-form-group" role="group" aria-labelledby={`${ids}-sops`}>
        <span className="resui-field-label" id={`${ids}-sops`}>SOPs<Optional /></span>
        <DocumentRows label="SOP" one="SOP" type="sop" values={sopIds} documents={documents} onChange={setSopIds} optional />
      </div>

      <div className="library-dialog-actions">
        <button type="button" className="resui-btn resui-btn--secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="resui-btn resui-btn--primary" disabled={!canSave} onClick={save}>
          Save scenario
        </button>
      </div>
    </LibraryDialog>
  );
}

export default ScenarioDialog;
